
import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { isEqual } from "lodash";

// Unified cache key
const LOCAL_PROFILE_KEY = "user_profile_cache";

interface UseUserProfileSyncProps {
  userId: string | undefined;
  enabled?: boolean;
  onProfileUpdated?: (profile: any) => void;
}

export default function useUserProfileSync({
  userId,
  enabled = true,
  onProfileUpdated,
}: UseUserProfileSyncProps) {
  const { user } = useAuth();
  const lastProfileRef = useRef<any>(null);

  // Get profile from local cache
  function getProfileFromLocalCache() {
    const raw = window.localStorage.getItem(LOCAL_PROFILE_KEY);
    try {
      if (raw) return JSON.parse(raw);
      return null;
    } catch {
      return null;
    }
  }

  // Save profile to local cache
  function saveProfileToLocalCache(profile: any) {
    try {
      window.localStorage.setItem(LOCAL_PROFILE_KEY, JSON.stringify(profile));
    } catch (error) {
      console.error('Error saving profile to cache:', error);
    }
  }

  // Handle profile data from AuthContext
  const handleProfileUpdate = useCallback((profile: any) => {
    if (!profile) return null;

    const previousProfile = lastProfileRef.current || getProfileFromLocalCache();
    
    // Check if meals_per_day specifically changed
    const mealsPerDayChanged = previousProfile?.meals_per_day !== profile.meals_per_day;
    const hasOtherChanges = !isEqual(profile, previousProfile);
    
    if (hasOtherChanges) {
      lastProfileRef.current = profile;
      saveProfileToLocalCache(profile);
      
      if (mealsPerDayChanged) {
        console.log('Meals per day changed from', previousProfile?.meals_per_day, 'to', profile.meals_per_day);
      }
      
      if (onProfileUpdated) {
        onProfileUpdated(profile);
      }
    }
    
    return profile;
  }, [onProfileUpdated]);

  // Force refresh profile data
  const forceRefresh = useCallback(async () => {
    if (user && String(user.id) === String(userId)) {
      const profileData = {
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
        avatar_url: user.avatarUrl,
        description: user.description,
        objectives: user.objectives,
        favorites: user.favorites,
        points: user.profile?.points || 0,
        gender: user.profile?.gender,
        age: user.profile?.age,
        weight: user.profile?.weight,
        height: user.profile?.height,
        activity_level: user.profile?.activityLevel,
        goal: user.profile?.goal,
        meals_per_day: user.profile?.mealsPerDay,
        target_calories: user.profile?.targetCalories,
        target_protein: user.profile?.targetProtein,
        target_carbs: user.profile?.targetCarbs,
        target_fat: user.profile?.targetFat,
        target_iron: user.profile?.targetIron,
        target_calcium: user.profile?.targetCalcium,
        target_zinc: user.profile?.targetZinc,
        target_magnesium: user.profile?.targetMagnesium,
        target_omega3: user.profile?.targetOmega3,
      };
      handleProfileUpdate(profileData);
    }
  }, [user, userId, handleProfileUpdate]);

  // Use profile from AuthContext - simplified
  useEffect(() => {
    if (!enabled || !userId || !user) return;
    
    if (String(user.id) === String(userId) && user.profile) {
      const profileData = {
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
        avatar_url: user.avatarUrl,
        description: user.description,
        objectives: user.objectives,
        favorites: user.favorites,
        points: user.profile.points || 0,
        gender: user.profile.gender,
        age: user.profile.age,
        weight: user.profile.weight,
        height: user.profile.height,
        activity_level: user.profile.activityLevel,
        goal: user.profile.goal,
        meals_per_day: user.profile.mealsPerDay,
        target_calories: user.profile.targetCalories,
        target_protein: user.profile.targetProtein,
        target_carbs: user.profile.targetCarbs,
        target_fat: user.profile.targetFat,
        target_iron: user.profile.targetIron,
        target_calcium: user.profile.targetCalcium,
        target_zinc: user.profile.targetZinc,
        target_magnesium: user.profile.targetMagnesium,
        target_omega3: user.profile.targetOmega3,
      };
      
      handleProfileUpdate(profileData);
    }
  }, [userId, enabled, user?.profile?.mealsPerDay, handleProfileUpdate]);

  // Get current profile helper
  const getCurrentProfile = useCallback(() => {
    if (user && String(user.id) === String(userId)) {
      return {
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
        avatar_url: user.avatarUrl,
        description: user.description,
        objectives: user.objectives,
        favorites: user.favorites,
        points: user.profile?.points || 0,
        gender: user.profile?.gender,
        age: user.profile?.age,
        weight: user.profile?.weight,
        height: user.profile?.height,
        activity_level: user.profile?.activityLevel,
        goal: user.profile?.goal,
        meals_per_day: user.profile?.mealsPerDay,
        target_calories: user.profile?.targetCalories,
        target_protein: user.profile?.targetProtein,
        target_carbs: user.profile?.targetCarbs,
        target_fat: user.profile?.targetFat,
        target_iron: user.profile?.targetIron,
        target_calcium: user.profile?.targetCalcium,
        target_zinc: user.profile?.targetZinc,
        target_magnesium: user.profile?.targetMagnesium,
        target_omega3: user.profile?.targetOmega3,
      };
    }
    return lastProfileRef.current || getProfileFromLocalCache();
  }, [user, userId]);

  return {
    getSyncedProfile: getCurrentProfile,
    refetch: forceRefresh,
  };
}
